import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { ROUTES, SITE_URL, type RouteKey } from "@/lib/routes";

interface SEOHeadProps {
  title: string;
  description: string;
  canonical?: string;
  ogType?: string;
  ogImage?: string;
  noIndex?: boolean;
  /**
   * When true, canonical + hreflang are emitted even if noIndex is set.
   * Use for noindex pages that still need a canonical (e.g. previews with
   * a defined locale variant). Off by default to preserve historical
   * behaviour on utility pages (404, /consultation-success, etc.).
   */
  forceCanonical?: boolean;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  /**
   * Alias for jsonLd, accepted for ergonomic parity with conventional SEOHead APIs.
   */
  schemaJson?: Record<string, unknown> | Record<string, unknown>[];
  /**
   * When provided, SEOHead emits canonical + hreflang alternates derived from the bilingual
   * route registry. Required for every /de/* and /en/* page.
   */
  routeKey?: RouteKey;
  /**
   * Optional explicit alternate URLs (used by blog posts where the slug
   * isn't part of the static route registry). When provided, hreflang
   * tags are emitted for whichever locales are present.
   */
  alternates?: { de?: string; en?: string };
}

const BASE_URL = SITE_URL;

/**
 * Per-route OG image mapping. Language is determined by URL prefix
 * (/en/* → -en.jpg, /de/* → DE filename). Pages without a routeKey
 * fall back to the homepage image in the active locale.
 */
const OG_IMAGE_BY_KEY: Record<RouteKey, { de: string; en: string }> = {
  home:         { de: "og-home.jpg",         en: "og-home-en.jpg" },
  services:     { de: "og-services.jpg",     en: "og-services-en.jpg" },
  website:      { de: "og-services.jpg",     en: "og-services-en.jpg" },
  booking:      { de: "og-services.jpg",     en: "og-services-en.jpg" },
  phone:        { de: "og-services.jpg",     en: "og-services-en.jpg" },
  about:        { de: "og-about.jpg",        en: "og-about-en.jpg" },
  contact:      { de: "og-contact.jpg",      en: "og-contact-en.jpg" },
  consultation: { de: "og-consultation.jpg", en: "og-consultation-en.jpg" },
  caseStudies:  { de: "og-case-studies.jpg", en: "og-case-studies-en.jpg" },
  blog:         { de: "og-case-studies.jpg", en: "og-case-studies-en.jpg" },
  privacy:      { de: "og-home.jpg",         en: "og-home-en.jpg" },
  imprint:      { de: "og-home.jpg",         en: "og-home-en.jpg" },
  terms:        { de: "og-home.jpg",         en: "og-home-en.jpg" },
  whoItIsFor:   { de: "og-services.jpg",     en: "og-services-en.jpg" },
};

function resolveOgImage(routeKey: RouteKey | undefined, locale: "de" | "en"): string {
  const file = routeKey ? OG_IMAGE_BY_KEY[routeKey][locale] : (locale === "en" ? "og-home-en.jpg" : "og-home.jpg");
  return `${BASE_URL}/${file}`;
}

export function SEOHead({
  title,
  description,
  canonical,
  ogType = "website",
  ogImage,
  noIndex = false,
  forceCanonical = false,
  jsonLd,
  schemaJson,
  routeKey,
  alternates,
}: SEOHeadProps) {
  const location = useLocation();
  const { language } = useLanguage();
  const image = ogImage || resolveOgImage(routeKey, language);

  const routeEntry = routeKey ? ROUTES.find((r) => r.key === routeKey) : undefined;
  const localizedPath = routeEntry ? routeEntry[language] : location.pathname;
  const canonicalUrl = canonical || `${BASE_URL}${localizedPath}`;
  const deUrl = alternates?.de ?? (routeEntry ? `${BASE_URL}${routeEntry.de}` : null);
  const enUrl = alternates?.en ?? (routeEntry ? `${BASE_URL}${routeEntry.en}` : null);

  const structured = jsonLd ?? schemaJson;
  const structuredString = structured
    ? JSON.stringify(Array.isArray(structured) ? (structured.length === 1 ? structured[0] : structured) : structured)
    : null;

  return (
    <Helmet>
      <html lang={language} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={noIndex ? "noindex, follow" : "index, follow"} />

      {/* Canonical + hreflang */}
      {(!noIndex || forceCanonical) && <link rel="canonical" href={canonicalUrl} />}
      {(!noIndex || forceCanonical) && deUrl && enUrl && (
        <link rel="alternate" hrefLang="de" href={deUrl} />
      )}
      {(!noIndex || forceCanonical) && deUrl && enUrl && (
        <link rel="alternate" hrefLang="en" href={enUrl} />
      )}
      {(!noIndex || forceCanonical) && deUrl && enUrl && (
        <link rel="alternate" hrefLang="x-default" href={deUrl} />
      )}
      {(!noIndex || forceCanonical) && !(deUrl && enUrl) && deUrl && (
        <link rel="alternate" hrefLang="de" href={deUrl} />
      )}
      {(!noIndex || forceCanonical) && !(deUrl && enUrl) && enUrl && (
        <link rel="alternate" hrefLang="en" href={enUrl} />
      )}

      {/* OpenGraph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content={language === "de" ? "de_DE" : "en_US"} />
      <meta property="og:site_name" content="OnePractix" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* JSON-LD */}
      {structuredString && (
        <script type="application/ld+json">{structuredString}</script>
      )}
    </Helmet>
  );
}
